/**
 * Three variants of the Users & Roles admin on /admin/users?variant=, fake grants.
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { PrototypeSwitcher } from '@/components/shared/PrototypeSwitcher';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { applyAction, seedRows } from './fake-store';
import type { PrototypeAction, PrototypeRow } from './types';
import { FALLBACK_HITS, SELF_EMAIL } from './types';
import { VariantA, VARIANT_A_NAME } from './VariantA';
import { VariantB, VARIANT_B_NAME } from './VariantB';
import { VariantC, VARIANT_C_NAME } from './VariantC';

const VARIANTS = [
  { key: 'A', name: VARIANT_A_NAME },
  { key: 'B', name: VARIANT_B_NAME },
  { key: 'C', name: VARIANT_C_NAME },
];

export function UsersRolesPrototypeHost() {
  const [searchParams] = useSearchParams();
  const variant = searchParams.get('variant') ?? 'A';
  const [rows, setRows] = useState<PrototypeRow[]>(() => seedRows());
  const [log, setLog] = useState<string[]>(['seeded']);
  const [stepUpOk, setStepUpOk] = useState(false);
  const [dialog, setDialog] = useState<string | null>(null);
  const [pending, setPending] = useState<PrototypeAction | null>(null);

  const run = (action: PrototypeAction) => {
    const needsStepUp =
      action.type === 'copy-link' ||
      action.type === 'delete' ||
      (action.type === 'revoke-admin' && action.emailKey === SELF_EMAIL);
    if (needsStepUp && !stepUpOk) {
      setDialog('step-up');
      setPending(action);
      return;
    }
    if (action.type === 'delete' || action.type === 'revoke-admin') {
      setPending(action);
      setDialog(action.type === 'delete' ? 'confirm-delete' : 'confirm-revoke');
      return;
    }
    commit(action);
  };

  const commit = (action: PrototypeAction) => {
    const result = applyAction(rows, action);
    if (result.error === 'deleted-409') {
      setDialog('deleted-409');
      setPending(action);
      setLog((prev) => [`409 ${action.type}`, ...prev]);
      return;
    }
    if (result.error) {
      setDialog(result.error);
      setLog((prev) => [result.log, ...prev]);
      return;
    }
    setRows(result.rows);
    setLog((prev) => [result.log, ...prev]);
    setDialog(null);
    setPending(null);
  };

  return (
    <div className="space-y-4 pb-24">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-2">
        <p className="text-sm text-muted-foreground">
          PROTOTYPE — fake grants. Fallback hits still on legacy:{' '}
          <span className="font-semibold text-foreground">{FALLBACK_HITS}</span>
          . Live tab: <code className="text-xs">/admin/users</code> (no variant).
        </p>
        <div className="flex items-center gap-2">
          <Switch
            id="step-up"
            checked={stepUpOk}
            onCheckedChange={setStepUpOk}
          />
          <Label htmlFor="step-up" className="text-sm">
            Step-up satisfied (auth_time ≤ 5 min)
          </Label>
        </div>
      </div>

      {variant === 'B' ? (
        <VariantB rows={rows} onAction={run} stepUpOk={stepUpOk} />
      ) : variant === 'C' ? (
        <VariantC rows={rows} onAction={run} stepUpOk={stepUpOk} />
      ) : (
        <VariantA rows={rows} onAction={run} stepUpOk={stepUpOk} />
      )}

      <details className="rounded-md border border-border bg-card p-3">
        <summary className="cursor-pointer text-sm font-medium">Prototype state</summary>
        <pre className="mt-2 max-h-64 overflow-auto text-xs text-muted-foreground">
          {JSON.stringify({ log: log.slice(0, 12), rows }, null, 2)}
        </pre>
      </details>

      <PrototypeSwitcher variants={VARIANTS} />

      <AlertDialog open={dialog === 'step-up'} onOpenChange={() => setDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Recent sign-in required</AlertDialogTitle>
            <AlertDialogDescription>
              Copy invite-link, Delete, self-demotion, and last-administrator change need
              auth_time ≤ 5 minutes. Flip the step-up switch to simulate a fresh sign-in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={dialog === 'confirm-delete' || dialog === 'confirm-revoke'}
        onOpenChange={() => setDialog(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {dialog === 'confirm-delete' ? 'Delete this email?' : 'Revoke this admin grant?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {dialog === 'confirm-delete'
                ? 'Delete keeps access off (Auth disabled, no roles). Restore is a separate action. There is no Ban.'
                : 'Subscription-sourced chips cannot be revoked here.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => pending && commit(pending)}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={dialog === 'deleted-409'} onOpenChange={() => setDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>This email is deleted</AlertDialogTitle>
            <AlertDialogDescription>
              Assign returns 409 until Restore. Restore first, then assign.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pending && 'emailKey' in pending) {
                  commit({ type: 'restore', emailKey: pending.emailKey });
                }
              }}
            >
              Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={dialog === 'last-admin' || dialog === 'self'}
        onOpenChange={() => setDialog(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {dialog === 'last-admin' ? 'Last administrator' : 'Self action refused'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              The module refuses this mutation. The UI explains it; it does not offer Ban or Unban.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>OK</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function isUsersRolesPrototypeVariant(value: string | null): boolean {
  return value === 'A' || value === 'B' || value === 'C';
}
